import {
  LEVEL_TAG_VARIANT,
  normalizeLevel,
} from "ctxReflectorV2/components/Logs/LogsData";

type LevelTagVariant = (typeof LEVEL_TAG_VARIANT)[keyof typeof LEVEL_TAG_VARIANT];

const KNOWN_LEVELS = new Set([
  "ERROR",
  "WARN",
  "WARNING",
  "INFO",
  "DEBUG",
  "TRACE",
  "FATAL",
  "CRITICAL",
]);

/**
 * Tag colour for a V1 log level, on the same scale as Reflector V2 Logs. V2's
 * `normalizeLevel` maps anything unrecognised to INFO, which would paint an
 * unknown level green; here it falls back to `neutral`. Never `default`: that
 * variant is raw `gray-*` in GenericTag and ignores the theme.
 */
export function levelTagVariant(level: string): LevelTagVariant {
  if (!KNOWN_LEVELS.has(level.trim().toUpperCase())) return "neutral";
  const variant = LEVEL_TAG_VARIANT[normalizeLevel(level)];
  // V2 paints TRACE `default` too; keep V1 on themed variants only.
  return variant === "default" ? "neutral" : variant;
}

// Every V1 line opens with `CTX - <LEVEL> - `, which repeats the Level tag
// shown next to it.
const CTX_LEVEL_PREFIX = /^CTX\s+-\s+[A-Z]+\s+-\s+/;

// An upper-snake identifier with at least one underscore
// (`PEER_ALIVE_ACTION_INF`), so a plain leading word like `GET` is never taken
// for one.
const LEADING_MSGID = /^([A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+)\s+([\s\S]*)$/;

/**
 * Splits a V1 log message for the table: drops the `CTX - <LEVEL> - ` prefix
 * and, when what follows is a MSGID, splits it off so it can show as a chip.
 * Real lines look like `CTX - INFO - KEEPALIVE_SEND_INF [MSGID=…] (60_039) …`
 * or `CTX - INFO - 10.228.172.121:53154 - GET /heartbeat HTTP/1.1 200`.
 * Copy keeps the original message; this only shapes what the cell shows.
 * `parseLogAttributes` (CTX-1356) starts from this split too.
 */
export function splitMsgId(message: string): {
  msgId: string | null;
  rest: string;
} {
  const body = message.replace(CTX_LEVEL_PREFIX, "");
  const match = body.match(LEADING_MSGID);
  return match
    ? { msgId: match[1], rest: match[2] }
    : { msgId: null, rest: body };
}
